import React, { useState, useRef, useEffect } from 'react';
import { Logo, getLogoUrlWithCacheBusting } from '../../api/logoApi';
import TendModal from './TendModal';

interface EditLogoModalProps {
  isOpen: boolean;
  onClose: () => void;
  logo: Logo;
  onSave: (logoId: number, updates: {
    file?: File;
    displayName?: string;
    logoKey?: string;
  }) => Promise<void>;
}

const EditLogoModal: React.FC<EditLogoModalProps> = ({
  isOpen,
  onClose,
  logo,
  onSave
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState(logo.displayName);
  const [logoKey, setLogoKey] = useState(logo.logoKey || '');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(
    logo.width && logo.height ? { width: logo.width, height: logo.height } : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // Reset form when logo changes
  useEffect(() => {
    setDisplayName(logo.displayName);
    setLogoKey(logo.logoKey || '');
    setImageDimensions(
      logo.width && logo.height ? { width: logo.width, height: logo.height } : null
    );
    setFile(null);
    setPreviewUrl(null);
    setError(null);
  }, [logo]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setError(null);

    if (!selectedFile) {
      return;
    }

    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('Invalid file type. Please upload a PNG, JPEG, or SVG image.');
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size too large. Maximum size is 5MB.');
      return;
    }

    setFile(selectedFile);

    // Generate preview and get image dimensions
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;
      setPreviewUrl(imageUrl);

      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) {
      setError('Display name cannot be empty.');
      return;
    }

    if (!logoKey.trim()) {
      setError('Logo key cannot be empty.');
      return;
    }

    if (!/^[a-z0-9_]+$/.test(logoKey)) {
      setError('Logo key must contain only lowercase letters, numbers, and underscores.');
      return;
    }

    try {
      setSaving(true);

      // Build updates object with only changed fields
      const updates: { file?: File; displayName?: string; logoKey?: string } = {};

      if (file) {
        updates.file = file;
      }

      if (displayName.trim() !== logo.displayName) {
        updates.displayName = displayName.trim();
      }

      if (logoKey.trim() !== logo.logoKey) {
        updates.logoKey = logoKey.trim();
      }

      // Only save if there are actual changes
      if (Object.keys(updates).length > 0) {
        await onSave(logo.id, updates);
      }

      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update logo');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDisplayName(logo.displayName);
    setLogoKey(logo.logoKey || '');
    setPreviewUrl(null);
    setImageDimensions(
      logo.width && logo.height ? { width: logo.width, height: logo.height } : null
    );
    setError(null);
    setSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  // Show current logo or preview of new upload
  // Apply cache-busting to current logo URL to ensure updated images are shown
  const currentImageUrl = previewUrl || getLogoUrlWithCacheBusting(logo);

  return (
    <TendModal title="Edit Logo" onClose={handleClose} width={460}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Current/Preview Image */}
        <div
          style={{
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,.10)',
            background: 'rgba(255,255,255,.05)',
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="tend-micro">{file ? 'New image preview' : 'Current image'}</span>
            {imageDimensions && (
              <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>
                {imageDimensions.width}×{imageDimensions.height}px
              </span>
            )}
          </div>
          <img
            src={currentImageUrl}
            alt={displayName}
            style={{ maxHeight: 128, margin: '0 auto', display: 'block', objectFit: 'contain' }}
          />
        </div>

        {/* File Input - Replace Image (Optional) */}
        <div>
          <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Replace image (optional)</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.svg"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: '100%',
              padding: '14px 12px',
              borderRadius: 12,
              border: '1px dashed rgba(255,255,255,.35)',
              background: 'none',
              color: file ? 'var(--text)' : 'var(--text-3)',
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 13,
              cursor: 'pointer',
              transition: 'border-color .2s, color .2s',
            }}
          >
            {file ? file.name : 'Click to select new image (PNG, JPEG, SVG)'}
          </button>
          <span className="tend-micro" style={{ display: 'block', marginTop: 4, textTransform: 'none', letterSpacing: 0.4 }}>
            Maximum file size: 5MB
          </span>
        </div>

        {/* Display Name Input */}
        <div>
          <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Display name</span>
          <input
            type="text"
            className="tend-ink-field"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter logo display name"
          />
        </div>

        {/* Logo Key Input */}
        <div>
          <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Logo key (identifier)</span>
          <input
            type="text"
            className="tend-ink-field"
            value={logoKey}
            onChange={(e) => setLogoKey(e.target.value.toLowerCase())}
            placeholder="e.g., holiday_2024"
          />
          <span className="tend-micro" style={{ display: 'block', marginTop: 4, textTransform: 'none', letterSpacing: 0.4 }}>
            Lowercase letters, numbers, and underscores only
          </span>
        </div>

        {/* Error Message */}
        {error && (
          <span className="tend-micro" style={{ color: 'var(--signal-rec-soft)', textTransform: 'none', letterSpacing: 0.4 }}>
            {error}
          </span>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button
            type="button"
            className="tend-btn tend-btn--glass"
            style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tend-btn tend-btn--commit"
            style={{ padding: '8px 16px', fontSize: 13 }}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </TendModal>
  );
};

export default EditLogoModal;
